import { useCallback, useMemo, useState, useEffect, useContext } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { GetQuantDirectoryTree } from '@trinity/chat/api/interfaces/projects.quant-folders';
import { queryKeys } from '@trinity/chat-components/query-keys';
import { useChatApiClient } from '../containers/Chat/useApiClient.js';
import { QuantAnalysisVersionContext } from '../containers/QuantAnalysis/context';
import { mapNodeToFolderTreeItem, findItemCurrentPath, extractFolderDestinations, type FolderOption, type QuantFolderTreeItem } from '../utils/quant-folders';
import { useInvalidateQuantFolders } from './useInvalidateQuantFolders';

export type UseMoveQuantFileReturn = {
  currentPath: string[];
  data: QuantFolderTreeItem | null;
  availableDestinations: FolderOption[];
  loadingTree: boolean;
  handleMove: () => Promise<void>;
  onSelectFolder: (folderId: string | null) => void;
  selectedFolderId: string | null;
};

type UseMoveQuantFileParams = {
  quantAnalysisId: number;
  projectId: number;
  itemId: number;
  quantAnalysisName?: string;
};

export const useMoveQuantFile = ({ quantAnalysisId, projectId, itemId, quantAnalysisName = 'All Analyses' }: UseMoveQuantFileParams): UseMoveQuantFileReturn => {
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const invalidateFolders = useInvalidateQuantFolders();
  const apiClient = useChatApiClient();
  const version = useContext(QuantAnalysisVersionContext);
  const versionId = version?.id;

  const onSelectFolder = useCallback((folderId: string | null) => {
    setSelectedFolderId(folderId);
  }, []);

  const query = useQuery<GetQuantDirectoryTree.Response>({
    queryKey: queryKeys.projects.quantAnalysis.directoryTree({ projectId, quantAnalysisId, versionId }),
    queryFn: () => {
      return apiClient.quantAnalysis.getQuantDirectoryTree({ quantAnalysisId, projectId, versionId });
    },
    enabled: !!(quantAnalysisId && projectId && versionId),
  });

  const tree = useMemo(() => {
    if (!query.data?.tree) return null;
    const roots = query.data.tree
      .map((node: GetQuantDirectoryTree.QuantNode) => mapNodeToFolderTreeItem(node))
      .filter(Boolean);

    if (roots.length === 0) return null;

    // create a synthetic root to hold all top-level folders
    const syntheticRoot: QuantFolderTreeItem = {
      id: 'root',
      name: quantAnalysisName,
      items: [],
      children: roots,
    };
    return syntheticRoot;
  }, [query.data?.tree, quantAnalysisName]);

  const currentPath: string[] = useMemo(() => {
    return findItemCurrentPath(query.data?.tree, itemId, quantAnalysisName);
  }, [query.data?.tree, itemId, quantAnalysisName]);

  // Get available destinations (excluding current location)
  const availableDestinations = useMemo(() => {
    if (!query.data?.tree) return [];

    // Create a full tree structure for destination lookup
    const fullTree = query.data.tree
      .map((node: GetQuantDirectoryTree.QuantNode) => mapNodeToFolderTreeItem(node))
      .filter(Boolean);

    const allFolderOptions: FolderOption[] = [];
    fullTree.forEach(root => {
      if (root) {
        const options = extractFolderDestinations(root, '', quantAnalysisName);
        allFolderOptions.push(...options);
      }
    });

    // Now filter based on current path
    if (currentPath && currentPath.length > 0) {
      const currentLocationPath = currentPath[0];

      const filteredOptions = allFolderOptions.filter(option => option.path !== currentLocationPath);

      if (currentLocationPath === quantAnalysisName) {
        // Current location is root, don't show root as a destination
        return filteredOptions;
      }

      const rootOption: FolderOption = {
        id: null,
        name: quantAnalysisName,
        path: quantAnalysisName,
      };

      return [rootOption, ...filteredOptions];
    }

    const rootOption: FolderOption = {
      id: null,
      name: quantAnalysisName,
      path: quantAnalysisName,
    };

    return [rootOption, ...allFolderOptions];
  }, [query.data?.tree, currentPath, quantAnalysisName]);

  // Auto-select the first available destination when data loads
  useEffect(() => {
    if (availableDestinations.length > 0) {
      const firstDestination = availableDestinations[0];
      setSelectedFolderId(firstDestination.id);
    }
  }, [availableDestinations]);

  const mutation = useMutation({
    mutationFn: () => {
      return apiClient.quantAnalysis.moveItemsToQuantFolder({
        projectId,
        quantAnalysisId,
        folderId: selectedFolderId ? +selectedFolderId : null,
        itemIds: [itemId],
      });
    },
    onSuccess: () => {
      invalidateFolders(quantAnalysisId, projectId, versionId);
    },
  });

  const handleMove = useCallback(() => {
    return mutation.mutateAsync().then(() => { });
  }, [mutation]);

  return {
    currentPath,
    data: tree,
    availableDestinations,
    loadingTree: query.isLoading || !tree,
    handleMove,
    onSelectFolder,
    selectedFolderId,
  };
};
